import {
  Badge,
  Body1Strong,
  Button,
  Caption1,
  Combobox,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  makeStyles,
  Option,
  Spinner,
  Subtitle1,
  tokens,
} from "@fluentui/react-components";
import { useMutation, useQuery } from "@tanstack/react-query";
import { type FormEvent, useMemo, useState } from "react";
import { ApiError } from "../api/client";
import { commerce, type StockUnit } from "../api/commerce";
import { baeTypography } from "../theme/baeTheme";
import { PHONE } from "../ui/tokens";

const useStyles = makeStyles({
  page: {
    display: "grid",
    gap: tokens.spacingVerticalL,
    alignContent: "start",
    maxWidth: "44rem",
    minWidth: 0,
  },
  heading: { fontFamily: baeTypography.fontFamilyDisplay, letterSpacing: "-0.02em" },
  panel: {
    display: "grid",
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    minWidth: 0,
    [PHONE]: { padding: tokens.spacingHorizontalM },
  },
  // A label column and a value column, until there is no room for two — then the label sits above
  // its value rather than squeezing a serial into 100px.
  facts: {
    display: "grid",
    gridTemplateColumns: "9rem 1fr",
    gap: tokens.spacingVerticalXS,
    minWidth: 0,
    [PHONE]: { gridTemplateColumns: "1fr", rowGap: "1px" },
  },
  mono: {
    fontFamily: baeTypography.fontFamilyMonospace,
    fontSize: "14px",
    // A serial is long and unbreakable at spaces. Allowed to wrap anywhere rather than push the
    // page wide.
    overflowWrap: "anywhere",
  },
  muted: { color: tokens.colorNeutralForeground3 },
  row: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalM,
    alignItems: "flex-end",
  },
  grow: { flexGrow: 1, minWidth: "12rem" },
});

const bucketColour = (bucket: string) =>
  bucket === "Available"
    ? ("success" as const)
    : bucket === "Damaged"
      ? ("danger" as const)
      : bucket === "Quarantine"
        ? ("warning" as const)
        : ("informative" as const);

/**
 * Look a unit up by its serial, and move it.
 *
 * Built around a scanner: the field takes focus, a scan ends with Enter, and the result appears.
 * Typing the serial by hand with or without hyphens finds the same unit, because the API matches
 * on the scan code.
 */
export function StockLookupPage() {
  const styles = useStyles();

  const [serial, setSerial] = useState("");
  const [unit, setUnit] = useState<StockUnit | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [target, setTarget] = useState("");

  const locations = useQuery({ queryKey: ["locations"], queryFn: commerce.locations });

  const destinations = useMemo(
    () =>
      (locations.data ?? []).filter(
        (l) =>
          l.canHoldStock &&
          l.isActive &&
          (!unit || l.siteId === siteIdOf(unit, locations.data ?? [])),
      ),
    [locations.data, unit],
  );

  const lookup = useMutation({
    mutationFn: (value: string) => commerce.unitBySerial(value),
    onSuccess: (found) => {
      setUnit(found);
      setError(null);
      setTarget("");
    },
    onError: (caught) => {
      setUnit(null);
      setError(caught instanceof ApiError ? caught.message : "Could not look that up.");
    },
  });

  const move = useMutation({
    mutationFn: () => commerce.putAway(unit!.serial, target),
    onSuccess: () => lookup.mutate(unit!.serial),
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : "Could not move that unit."),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (serial.trim()) lookup.mutate(serial.trim());
  };

  return (
    <div className={styles.page}>
      <Subtitle1 className={styles.heading} as="h2">
        Find a unit
      </Subtitle1>

      <div className={styles.panel}>
        <form onSubmit={submit}>
          <Field label="Scan or type a serial" hint="Hyphens are optional.">
            <Input
              className={styles.mono}
              value={serial}
              autoFocus
              placeholder="LKO-2609-000001-4"
              onChange={(_, d) => setSerial(d.value)}
            />
          </Field>
        </form>
      </div>

      {lookup.isPending && <Spinner size="tiny" label="Looking up" />}

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {unit && !unit.isInStock && (
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>This unit is no longer in stock</MessageBarTitle>
            {unit.leftStockBecause}
            {unit.leftStockAt ? ` on ${new Date(unit.leftStockAt).toLocaleDateString()}.` : "."} The
            label is still readable, which is why the scan found it — but the piece is not stock any
            more and cannot be moved.
          </MessageBarBody>
        </MessageBar>
      )}

      {unit && (
        <div className={styles.panel}>
          <div className={styles.facts}>
            <Caption1 className={styles.muted}>Serial</Caption1>
            <Body1Strong className={styles.mono}>{unit.serial}</Body1Strong>

            <Caption1 className={styles.muted}>SKU</Caption1>
            <span className={styles.mono}>{unit.sku}</span>

            <Caption1 className={styles.muted}>State</Caption1>
            <span>
              {unit.isInStock ? (
                <Badge appearance="filled" color={bucketColour(unit.bucket)}>
                  {unit.bucket}
                </Badge>
              ) : (
                /* Not the bucket. Showing "Available" for a written-off unit is the one answer
                   this screen must never give. */
                <Badge appearance="filled" color="danger">
                  Gone
                </Badge>
              )}
            </span>

            <Caption1 className={styles.muted}>Site</Caption1>
            <span className={styles.mono}>{unit.siteCode}</span>

            <Caption1 className={styles.muted}>Location</Caption1>
            <span className={styles.mono}>
              {unit.storageLocationCode ?? <span className={styles.muted}>not put away yet</span>}
            </span>

            <Caption1 className={styles.muted}>Received</Caption1>
            <span>{new Date(unit.receivedAt).toLocaleString()}</span>
          </div>

          <div className={styles.row} hidden={!unit.isInStock}>
            <Field label="Move it to" className={styles.grow}>
              <Combobox
                placeholder="Pick a location"
                value={destinations.find((l) => l.id === target)?.name ?? ""}
                onOptionSelect={(_, data) => setTarget(data.optionValue ?? "")}
              >
                {destinations.map((location) => (
                  <Option key={location.id} value={location.id} text={location.name}>
                    {location.code} — {location.name}
                  </Option>
                ))}
              </Combobox>
            </Field>

            <Button
              appearance="primary"
              disabled={!target || move.isPending}
              onClick={() => move.mutate()}
            >
              {move.isPending ? <Spinner size="tiny" /> : "Put away"}
            </Button>
          </div>

          <Caption1 className={styles.muted}>
            Only locations inside this unit's own site are offered. Moving it between sites is a
            transfer, not a put-away.
          </Caption1>
        </div>
      )}
    </div>
  );
}

/** The site a unit is at, resolved from its site code. */
function siteIdOf(
  unit: StockUnit,
  locations: { id: string; code: string; isSite: boolean }[],
): string {
  return locations.find((l) => l.isSite && l.code === unit.siteCode)?.id ?? "";
}
